<template>
  <div>
    <div class="custom-editable-node">
      <Handle
        v-bind:key="handle.id"
        v-for="handle in inputHandles"
        :id="handle.id"
        type="target"
        :position="Position.Left"
        :style="handle.style"
        @mouseover="getLabel(handle.id)"
        @mouseenter="showLabel"
        @mouseleave="hideLabel"
      />
      <div>
        <strong>{{ props.data.orchName }} </strong>
        <font-awesome-icon
          v-if="props.data.validity === 'invalid'"
          icon="exclamation-circle"
          size="lg"
          style="margin: 5px; color: red"
        />
        <font-awesome-icon
          v-if="props.data.validity === 'pending'"
          icon="question-circle"
          size="lg"
          style="margin: 5px; color: blue"
        />
        <font-awesome-icon
          v-if="props.data.validity === 'valid'"
          icon="check-circle"
          size="lg"
          style="margin: 5px; color: green"
        />
      </div>
      <Handle
        v-bind:key="handle.id"
        v-for="handle in outputHandles"
        :id="handle.id"
        type="source"
        :position="Position.Right"
        :style="handle.style"
        @mouseover="getLabel(handle.id)"
        @mouseenter="showLabel"
        @mouseleave="hideLabel"
      />
    </div>
    <p v-if="hoverOverHandle" class="custom-handle-label">
      {{ currentHandleLabel }}
    </p>
    <div class="comp_output">
        <div style="width: 100px; height: 100px;">
          <font-awesome-icon
          v-if="props.data.runstatus.status === 'setup'"
          icon="screwdriver-wrench"
          style="margin: 5px"
          class="fa-3x"/>      
          <font-awesome-icon
          v-if="props.data.runstatus.status === 'compute'"
          icon="gear"
          style="margin: 5px; --fa-animation-duration: 3s;"
          class="fa-3x fa-spin"/>   
          <font-awesome-icon
          v-if="props.data.runstatus.status === 'error'"
          icon="exclamation-triangle"
          style="margin: 5px"
          class="fa-3x fa-fade"/> 
        </div>
          <p> {{$t('message.Last_event')}} @ {{props.data.runstatus.time}}</p>
          <p> {{$t('message.Current_iteration')}}  # {{props.data.runstatus.iterations}} </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  CSSProperties,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  inject
} from "vue";
import { Handle, Position, Connection, Edge, NodeProps } from "@braks/vue-flow";
interface ColorSelectorNodeProps extends NodeProps {
  id: string;
  data: {
    orchName: string;
    orchApi: string;
    validity: string;
    runstatus: {
      status: string;
      time: string;
      iterations?: number;
    };
    orchOptions?: object;
    orchParameters?: object;
    orchInputs?: object;
    orchOutputs?: object;
  };
  type: string;
  selected?: boolean;
  connectable?: boolean;
  xPos?: number;
  yPos?: number;
  targetPosition?: Position;
  sourcePosition?: Position;
  dragging?: boolean;
}
const emit = defineEmits(["updateHandles"]);
const props = defineProps<ColorSelectorNodeProps>();
const targetHandleStyle: CSSProperties = { background: "rgb(66, 66, 66)" };
const offsetInc = (dataObject: object, index: number) =>
  (1 / (Object.keys(dataObject).length + 1)) * 100 * (index + 1);
const currentHandleLabel = ref();
let hoverOverHandle = ref(false);
const results = ref();
let isLoading = false;
let error = null;
let timer;
let showResult = ref(false);
const userAPIEndPoint = inject('back_end_user_services_API_end_point');
const userJWTToken = inject('userJWTToken');

const getHandles = function (data, handles) {
  for (let i = 0; i <= Object.keys(data).length - 1; i++) {
    const name: string = Object.keys(data)[i];

    // parametric data connections have different style
    // const isParam: boolean = data[name] === "implicit";
    const offsetIncVal = offsetInc(data, i);
    handles.push({
      id: name,
      value: name,
      // isParam: isParam,
      style: {
        ...targetHandleStyle,
        top: offsetIncVal.toString() + "%",
      },
      data: { offsetTop: offsetIncVal },
    });
  }
  return handles;
};

const outputHandles = computed(() =>
  props.data.orchOutputs ? getHandles(props.data.orchOutputs, []) : []
);
const inputHandles = computed(() =>
  props.data.orchInputs ? getHandles(props.data.orchInputs, []) : []
);

const setDriver = function (data) {
  if (data !== "{}" && "isDriver" in data) return data["isDriver"];
  return false;
  };
const isDriver = props.data.orchOptions
  ? setDriver(props.data.orchOptions)
  : false;

watch(
  () => [props.data.orchOutputs, props.data.orchInputs],
  (newval, oldval) => {
    console.log("updated handles event triggered for ", props.data.orchName);
    emit("updateHandles", [
      { id: props.id, handles: outputHandles, type: "source" },
      { id: props.id, handles: inputHandles, type: "target" },
    ]);
  }
);

const onConnect = (params: Connection | Edge) =>
  console.log("handle onConnect", params);

const getLabel = (id) => (currentHandleLabel.value = id);
const showLabel = () => (hoverOverHandle.value = true);
const hideLabel = () => (hoverOverHandle.value = false);

const getStatus = async () => {
  isLoading = true;
  error = null;
  console.log("STATUS orch");
  await fetch(userAPIEndPoint + "/status", {
    method: "GET",
    headers: { "Authorization": `Bearer ${userJWTToken.value}` }
    }).then((response) => {
      if (response.ok) {
        return response.json();
      }
    }).then((data) => {
      isLoading = false;
      results.value = data.response;
      console.log(data.response);
      if (props.data.orchName in results.value) {showResult.value = true;}
    }).catch((error) => {
      console.log(error);
      isLoading = false;
      error = "Failed to fetch data - please try again later.";
    });
};
const cancelAutoUpdate = () => clearInterval(timer);

// TODO design better display of node status information
// TODO - status calls are currently repeated for every additional component >> split the api call and results assignment - do the api call in Custom Editable Node and provide outputs to children    
// from the server at regular intervals and fails if the server cna't be reached
onMounted(() => {
  // getStatus();
  // timer = setInterval(getStatus, 10000);
});
onUnmounted(() => cancelAutoUpdate());

</script>

<style scoped>
.custom-editable-node {
  background-color: rgb(197, 197, 197);
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  font-size: 20px;
}

.custom-handle-label {
  color:rgb(56, 56, 56);
  padding: 0;
  margin: 0;
  position: absolute;
  bottom: -25px;
  width: 100%;
  height: 20px;
  font-size: 20px;
}

.comp_output {
  color:rgb(197, 197, 197);
  padding: 0;
  margin: 0;
  position: absolute;
  top: 20%;
  right: 10%;
  width: 80%;
  font-size: 20px;
}
</style>
